(() => {
  'use strict';

  const formatDate = value => {
    if (!value) return 'Not recorded';

    const parts = value.split('-');
    if (parts.length !== 3) return value;

    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  };

  const clean = value => String(value || '').trim();

  const escapeHtml = value =>
    clean(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const valueOf = (form, name) => {
    const field = form.elements.namedItem(name);

    if (!field) return '';

    if (field instanceof RadioNodeList) {
      return clean(field.value);
    }

    return clean(field.value);
  };

  const initTool = root => {
    if (!root || root.dataset.seqPmReady === 'true') return;
    root.dataset.seqPmReady = 'true';

    const form = root.querySelector('[data-pm-form]');
    const printButton = root.querySelector('[data-pm-print]');
    const resetButton = root.querySelector('[data-pm-reset]');
    const validation = root.querySelector('[data-pm-validation]');
    const progressText = root.querySelector('[data-pm-progress-text]');
    const progressBar = root.querySelector('[data-pm-progress-bar]');
    const printSheet = root.querySelector('[data-pm-print-sheet]');
    const checks = Array.from(root.querySelectorAll('[data-pm-check-item]'));

    if (!form || !printSheet) return;

    const dateField = form.querySelector('[data-pm-date]');

    if (dateField && !dateField.value) {
      const now = new Date();
      const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
      dateField.value = local.toISOString().slice(0, 10);
    }

    const syncSignoff = () => {
      const inspectedBy = valueOf(form, 'inspected_by');
      const signoff = form.elements.namedItem('signoff_name');

      if (signoff && !clean(signoff.value) && inspectedBy) {
        signoff.value = inspectedBy;
      }
    };

    const updateProgress = () => {
      const completed = checks.filter(row =>
        row.querySelector('input[type="radio"]:checked')
      ).length;

      const total = checks.length || 1;
      const percent = Math.round((completed / total) * 100);

      if (progressText) {
        progressText.textContent = `${completed} of ${checks.length} checks completed`;
      }

      if (progressBar) {
        progressBar.style.width = `${percent}%`;
      }
    };

    const validateCoreFields = () => {
      const required = Array.from(form.querySelectorAll('[data-pm-required]'));
      const missing = [];

      required.forEach(field => {
        const wrapper = field.closest('.seq-pm-tool__field');
        const empty = !clean(field.value);

        wrapper?.classList.toggle('is-invalid', empty);
        if (empty) missing.push(field);
      });

      return missing;
    };

    const setValidationMessage = (message, isError = false) => {
      if (!validation) return;

      validation.textContent = message;
      validation.classList.toggle('is-visible', Boolean(message));
      validation.classList.toggle('is-error', isError);
    };

    const metaItems = [
      ['Property address', 'property_address', false],
      ['Inspection date', 'inspection_date', true],
      ['Inspection type', 'inspection_type', false],
      ['Agency / property manager', 'agency', false],
      ['Inspected by', 'inspected_by', false],
      ['Unit location', 'unit_location', false],
      ['Brand / model', 'brand_model', false],
      ['Property / tenancy reference', 'property_reference', false],
      ['Split systems at property', 'unit_count', false]
    ];

    const maintenanceItems = [
      ['Last known filter clean', 'last_filter_clean', false],
      ['Last known internal deep clean', 'last_deep_clean', false],
      ['Last known technical service', 'last_technical_service', false],
      ['Next review / action date', 'next_review_date', true]
    ];

    const buildPrintSheet = () => {
      const meta = printSheet.querySelector('[data-print-meta]');
      const printChecks = printSheet.querySelector('[data-print-checks]');
      const printOutcome = printSheet.querySelector('[data-print-outcome]');
      const printStatus = printSheet.querySelector('[data-print-status]');
      const printPhotos = printSheet.querySelector('[data-print-photos]');
      const printMaintenance = printSheet.querySelector('[data-print-maintenance]');
      const printNotes = printSheet.querySelector('[data-print-notes]');
      const printSignoff = printSheet.querySelector('[data-print-signoff]');

      if (meta) {
        meta.innerHTML = metaItems
          .map(([label, name, isDate]) => {
            let value = valueOf(form, name) || 'Not recorded';
            if (isDate && value !== 'Not recorded') value = formatDate(value);

            return `
              <div>
                <span>${escapeHtml(label)}</span>
                <strong>${escapeHtml(value)}</strong>
              </div>
            `;
          })
          .join('');
      }

      if (printChecks) {
        printChecks.innerHTML = checks
          .map(row => {
            const label = row.dataset.checkLabel || '';
            const guidance = row.dataset.checkGuidance || '';
            const selected = row.querySelector('input[type="radio"]:checked');
            const photo = row.querySelector('input[type="checkbox"]');

            return `
              <tr>
                <td>${escapeHtml(label)}</td>
                <td>${escapeHtml(selected?.value || 'Not checked')}</td>
                <td>${photo?.checked ? 'Yes' : 'No'}</td>
                <td>${escapeHtml(guidance)}</td>
              </tr>
            `;
          })
          .join('');
      }

      if (printOutcome) {
        printOutcome.textContent =
          valueOf(form, 'recommended_action') || 'Not selected';
      }

      if (printStatus) {
        printStatus.textContent =
          valueOf(form, 'inspection_status') || 'Inspection complete';
      }

      if (printPhotos) {
        const selectedPhotos = Array.from(
          form.querySelectorAll('input[name="photo_record"]:checked')
        ).map(field => field.value);

        printPhotos.textContent = selectedPhotos.length
          ? selectedPhotos.join(' • ')
          : 'No supporting photo categories recorded.';
      }

      if (printMaintenance) {
        printMaintenance.innerHTML = maintenanceItems
          .map(([label, name, isDate]) => {
            let value = valueOf(form, name) || 'Not recorded';
            if (isDate && value !== 'Not recorded') value = formatDate(value);

            return `
              <div>
                <span>${escapeHtml(label)}</span>
                <strong>${escapeHtml(value)}</strong>
              </div>
            `;
          })
          .join('');
      }

      if (printNotes) {
        printNotes.textContent =
          valueOf(form, 'inspection_notes') || 'No additional notes recorded.';
      }

      if (printSignoff) {
        const inspector =
          valueOf(form, 'signoff_name') ||
          valueOf(form, 'inspected_by') ||
          'Not recorded';

        const generated = new Date().toLocaleString('en-AU', {
          dateStyle: 'medium',
          timeStyle: 'short'
        });

        printSignoff.innerHTML = `
          <div>
            <span>Inspector / representative</span>
            <strong>${escapeHtml(inspector)}</strong>
          </div>
          <div>
            <span>Record generated</span>
            <strong>${escapeHtml(generated)}</strong>
          </div>
        `;
      }
    };

    const printInspection = () => {
      syncSignoff();

      const missing = validateCoreFields();

      if (missing.length) {
        setValidationMessage(
          'Complete the highlighted property, date, inspector and unit-location fields before saving the inspection.',
          true
        );

        missing[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        window.setTimeout(() => missing[0].focus(), 350);
        return;
      }

      buildPrintSheet();
      setValidationMessage(
        'Completed record ready. Choose your printer or “Save as PDF” in the print window.'
      );

      document.body.classList.add('seq-pm-printing');

      window.requestAnimationFrame(() => {
        window.print();
      });
    };

    const resetInspection = () => {
      const hasData =
        Array.from(form.elements).some(field => {
          if (!('type' in field)) return false;

          if (field.type === 'radio' || field.type === 'checkbox') {
            return field.checked;
          }

          return Boolean(clean(field.value));
        });

      if (
        hasData &&
        !window.confirm(
          'Start a new inspection? This will clear the information currently entered on this page.'
        )
      ) {
        return;
      }

      form.reset();

      if (dateField) {
        const now = new Date();
        const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
        dateField.value = local.toISOString().slice(0, 10);
      }

      form.querySelectorAll('.is-invalid').forEach(item =>
        item.classList.remove('is-invalid')
      );

      setValidationMessage('');
      updateProgress();

      form.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    };

    form.addEventListener('change', event => {
      if (event.target.matches('[data-pm-required]')) {
        event.target
          .closest('.seq-pm-tool__field')
          ?.classList.remove('is-invalid');
      }

      if (event.target.name === 'inspected_by') {
        syncSignoff();
      }

      updateProgress();
    });

    form.addEventListener('input', event => {
      if (event.target.matches('[data-pm-required]')) {
        event.target
          .closest('.seq-pm-tool__field')
          ?.classList.remove('is-invalid');
      }

      if (event.target.name === 'inspected_by') {
        syncSignoff();
      }
    });

    printButton?.addEventListener('click', printInspection);
    resetButton?.addEventListener('click', resetInspection);

    window.addEventListener('beforeprint', buildPrintSheet);

    window.addEventListener('afterprint', () => {
      document.body.classList.remove('seq-pm-printing');
    });

    updateProgress();
    syncSignoff();
  };

  const initAll = () => {
    document.querySelectorAll('[data-seq-pm-tool]').forEach(initTool);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', event => {
    event.target
      .querySelectorAll?.('[data-seq-pm-tool]')
      .forEach(initTool);
  });
})();
